import React, { useCallback } from 'react';

import { MAX_METADATA_IMPORT_UPLOAD_MB } from '@aline/common';
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  DialogActions,
  DialogContent,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid-premium';
import {
  DataGridPremium,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GRID_CHECKBOX_SELECTION_FIELD,
} from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import { Check, CloudUpload, Download, FileSpreadsheet, X } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import type { FileRejection } from 'react-dropzone';
import { AlineInput } from 'src/AlineInput';
import {
  AlineAlert,
  AlineAutocompleteInput,
  AlineButton,
  AlineCheckbox,
  AlineDialog,
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components';
import { AlineTooltip } from 'src/components/generic/AlineTooltip';
import type {
  DocumentMetadataImportFieldsFragment,
  DocumentMetadataImportPreviewFieldsFragment,
  GetExtractionQuestionsQuery,
} from 'src/gql/generated/apollo-types';
import {
  DocumentMetadataImportStatus,
  ExtractionQuestionType,
} from 'src/gql/generated/apollo-types';
import { createDataGridPremiumFooterlessPaperStyles } from 'src/hooks';
import { useGetExtractionQuestions } from 'src/hooks/extraction/extraction';
import { makeStyles } from 'tss-react/mui';

import { useImportDataWizard } from './useImportDataWizard';
import { getEmptyColumnsWarningMessage, SPREADSHEET_MIME_TYPE } from './wizard-import.helpers';

interface ImportDataWizardProps {
  onClose: () => void;
  open: boolean;
  existingImport?: DocumentMetadataImportFieldsFragment | null;
  initialOperationError?: string | null;
  onConfirmValidationError?: (options: {
    errorMessage: string;
    importRecord: DocumentMetadataImportFieldsFragment;
  }) => void;
}

type ExtractionQuestionOption = GetExtractionQuestionsQuery['extractionQuestions'][number];

interface DownloadTemplateQuestionOption extends ExtractionQuestionOption {
  label: string;
}

interface DownloadTemplateStepProps {
  displayName: string;
  extractionQuestions: ExtractionQuestionOption[];
  onDisplayNameChange: (value: string) => void;
  onSelectedQuestionIdsChange: (questionIds: string[]) => void;
  questionsLoading: boolean;
  selectedQuestionIds: string[];
  readOnly?: boolean;
}

interface UploadSpreadsheetStepProps {
  onClearUploadedFile: () => void;
  onDownloadTemplate: () => void;
  onFileSelect: (file: File) => void;
  uploadedFile: File | null;
  readOnly?: boolean;
}

interface ImportDataWizardFooterProps {
  actions: {
    onBack: () => void;
    onClose: () => void;
    onConfirmImport: () => void;
    onNextFromDownload: () => void;
    onUploadAndValidate: () => void;
  };
  state: ImportDataWizardState;
}

interface ImportDataWizardStepProps {
  extractionQuestions: ExtractionQuestionOption[];
  questionsLoading: boolean;
  wizard: ReturnType<typeof useImportDataWizard>;
  wizardState: ImportDataWizardState;
}

interface ImportValidationAlert {
  message: React.ReactNode;
  severity: 'error' | 'success' | 'warning';
  action?: React.ReactNode;
  onClose?: () => void;
}

interface WizardProgressProps {
  activeStep: number;
  allCompleted: boolean;
  steps: readonly { label: string }[];
}

interface ImportDataWizardState {
  canConfirmImport: boolean;
  canGoNextFromDownload: boolean;
  canGoNextFromUpload: boolean;
  confirming: boolean;
  generating: boolean;
  isFailedImport: boolean;
  isProcessing: boolean;
  isReadOnly: boolean;
  showDownloadFooter: boolean;
  showPreviewFooter: boolean;
  showUploadFooter: boolean;
  title: string;
  uploading: boolean;
  wizardSteps: readonly { label: string }[];
}

const MAX_VISIBLE_PROPERTY_CHIPS = 6;
const WIZARD_STEPS = [{ label: 'Setup' }, { label: 'Upload' }, { label: 'Review' }] as const;

const QUESTION_TYPE_LABELS = {
  [ExtractionQuestionType.Boolean]: 'Boolean',
  [ExtractionQuestionType.Date]: 'Date',
  [ExtractionQuestionType.MultiSelect]: 'Multi select',
  [ExtractionQuestionType.Number]: 'Number',
  [ExtractionQuestionType.SingleSelect]: 'Single select',
  [ExtractionQuestionType.Text]: 'Text',
} satisfies Record<ExtractionQuestionType, string>;

const useStepHeaderStyles = makeStyles()(theme => ({
  description: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  title: {
    color: theme.appColors.iconsType,
    fontSize: 16,
    fontWeight: 600,
  },
}));

const useDownloadTemplateStepStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  fieldLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  formFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
  },
  howItWorksItem: {
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
  },
  howItWorksNumber: {
    alignItems: 'center',
    backgroundColor: theme.appColors.hoverBackground,
    borderRadius: '50%',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    height: 18,
    justifyContent: 'center',
    marginTop: 1,
    width: 18,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  sidebarCard: {
    backgroundColor: theme.appColors.background2,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2.5),
  },
  sidebarCardHeader: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  twoColumnGrid: {
    alignItems: 'start',
    display: 'grid',
    flex: 1,
    gap: theme.spacing(4),
    gridTemplateColumns: '7fr 5fr',
  },
}));

const useUploadSpreadsheetStepStyles = makeStyles<void, 'iconCircle'>()(
  (theme, _params, classes) => ({
    content: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(3),
      width: '100%',
    },
    dropzone: {
      '&:hover': {
        backgroundColor: theme.appColors.hoverBackground,
        borderColor: theme.appColors.primary,
      },
      alignItems: 'center',
      backgroundColor: theme.appColors.background2,
      border: `1.5px dashed ${theme.appColors.rule}`,
      borderRadius: 14,
      cursor: 'pointer',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(2),
      justifyContent: 'center',
      minHeight: 240,
      padding: theme.spacing(4),
      transition: 'background-color 200ms ease, border-color 200ms ease',
      width: '100%',
    },
    dropzoneActive: {
      [`& .${classes.iconCircle}`]: {
        backgroundColor: theme.palette.common.white,
      },
      backgroundColor: theme.appColors.selectedBackgroundNav,
      borderColor: theme.appColors.primary,
    },
    dropzoneBrowse: {
      textDecoration: 'underline',
      textUnderlineOffset: 2,
    },
    dropzoneDescription: {
      color: theme.palette.text.secondary,
      textAlign: 'center',
    },
    dropzoneHint: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      textAlign: 'center',
    },
    dropzoneStatic: {
      '&:hover': {
        backgroundColor: theme.appColors.background2,
        borderColor: theme.appColors.rule,
      },
      cursor: 'default',
    },
    dropzoneTitle: {
      color: theme.appColors.iconsType,
      fontSize: 16,
      textAlign: 'center',
    },
    fileIconCircle: {
      alignItems: 'center',
      backgroundColor: alpha(theme.palette.success.main, 0.1),
      borderRadius: '50%',
      color: theme.palette.success.main,
      display: 'flex',
      height: 80,
      justifyContent: 'center',
      width: 80,
    },
    fileMeta: {
      color: theme.palette.text.secondary,
      fontSize: 12,
    },
    iconCircle: {
      alignItems: 'center',
      backgroundColor: theme.appColors.hoverBackground,
      borderRadius: '50%',
      color: theme.appColors.primary,
      display: 'flex',
      height: 80,
      justifyContent: 'center',
      transition: 'background-color 200ms ease',
      width: 80,
    },
  })
);

const useFooterStyles = makeStyles()(theme => ({
  actions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    minHeight: 80,
  },
  footerButton: {
    '&&': {
      minWidth: 120,
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
  },
}));

const useWizardStyles = makeStyles()(theme => ({
  progressBand: {
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'center',
    padding: theme.spacing(1.5, 5, 2.5),
    width: '100%',
  },
  title: {
    color: theme.appColors.iconsType,
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  wizardHeader: {
    '&&': {
      alignItems: 'center',
      padding: theme.spacing(3.5, 5, 1),
    },
    '&& .MuiTypography-h1': {
      margin: 0,
    },
    '&& button': {
      alignSelf: 'center',
    },
  },
  wizardPaper: {
    display: 'flex',
    flexDirection: 'column',
    height: 'min(640px, 90vh)',
    maxHeight: '90vh',
    maxWidth: 960,
    overflow: 'hidden',
    width: '100%',
  },
}));

const useWizardProgressStyles = makeStyles()(theme => ({
  connector: {
    backgroundColor: theme.appColors.rule,
    flex: 1,
    height: 1,
    minWidth: theme.spacing(3),
    transition: 'background-color 250ms ease',
  },
  connectorDone: {
    backgroundColor: theme.appColors.primary,
  },
  indicator: {
    alignItems: 'center',
    backgroundColor: 'transparent',
    border: `1.5px solid ${theme.appColors.rule}`,
    borderRadius: '50%',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexShrink: 0,
    fontSize: 10,
    fontWeight: 600,
    height: 22,
    justifyContent: 'center',
    transition: 'background-color 250ms ease, border-color 250ms ease, color 250ms ease',
    width: 22,
  },
  indicatorActive: {
    backgroundColor: theme.palette.common.white,
    borderColor: theme.appColors.primary,
    color: theme.appColors.primary,
  },
  indicatorCompleted: {
    backgroundColor: theme.appColors.primary,
    borderColor: theme.appColors.primary,
    color: theme.palette.common.white,
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  labelActive: {
    color: theme.appColors.iconsType,
    fontWeight: 600,
  },
  labelCompleted: {
    color: theme.appColors.iconsType,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
    width: '100%',
  },
  step: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

export function ImportDataWizard({
  existingImport,
  initialOperationError = null,
  onClose,
  onConfirmValidationError,
  open,
}: ImportDataWizardProps): JSX.Element {
  const { classes } = useWizardStyles();
  const wizard = useImportDataWizard({
    existingImport,
    initialOperationError,
    onClose,
    onConfirmValidationError,
    open,
    stepCount: 3,
  });
  const { extractionQuestions, loading: questionsLoading } = useGetExtractionQuestions({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const { requestClose: handleWizardRequestClose, uploadedFile } = wizard;
  const wizardState = getImportDataWizardState({
    activeStep: wizard.activeStep,
    confirming: wizard.confirming,
    displayName: wizard.displayName,
    generating: wizard.generating,
    selectedQuestionIds: wizard.selectedQuestionIds,
    uploadResult: wizard.uploadResult,
    uploadedFile,
    uploading: wizard.uploading,
  });

  return (
    <>
      <AlineDialog
        classes={{ paper: classes.wizardPaper }}
        contentPaddingPx={0}
        contentPaddingTopPx={0}
        headerClassName={classes.wizardHeader}
        maxWidth="md"
        open={open}
        title={wizardState.title}
        onClose={handleWizardRequestClose}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
            p: 0,
          }}
        >
          <Box className={classes.progressBand}>
            <WizardProgress
              activeStep={wizard.activeStep}
              allCompleted={wizardState.isReadOnly}
              steps={wizardState.wizardSteps}
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              minHeight: 0,
              overflowY: wizard.activeStep === 2 ? 'hidden' : 'auto',
              pt: 1,
              px: 5,
            }}
          >
            {wizard.operationError && wizard.activeStep !== 2 && (
              <AlineAlert severity="error" sx={{ mb: 2 }} onClose={wizard.clearOperationError}>
                {wizard.operationError}
              </AlineAlert>
            )}
            <ImportDataWizardStep
              extractionQuestions={extractionQuestions}
              questionsLoading={questionsLoading}
              wizard={wizard}
              wizardState={wizardState}
            />
          </Box>
        </DialogContent>
        <ImportDataWizardFooter
          actions={{
            onBack: wizard.goBack,
            onClose: handleWizardRequestClose,
            onConfirmImport: wizard.handleConfirmImport,
            onNextFromDownload: wizard.handleDownloadTemplateAndContinue,
            onUploadAndValidate: wizard.handleUploadAndValidate,
          }}
          state={wizardState}
        />
      </AlineDialog>
      <ConfirmationDialog
        message="Your data migration is being submitted. Keep this window open until it finishes, or close to return to the history list. If you close now, the migration may still start in the background."
        open={wizard.closeWhileConfirmingOpen}
        primaryText="Keep waiting"
        secondaryText="Close anyway"
        title="Import in progress"
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={wizard.dismissCloseWhileConfirming}
        onClickSecondary={wizard.closeWizardWhileConfirming}
        onClose={wizard.dismissCloseWhileConfirming}
      />
    </>
  );
}

function ImportDataWizardStep({
  extractionQuestions,
  questionsLoading,
  wizard,
  wizardState,
}: ImportDataWizardStepProps): JSX.Element | null {
  const { clearUploadedFile, uploading } = wizard;
  const handleClearUploadedFile = useCallback(
    () => clearUploadedFile(uploading),
    [clearUploadedFile, uploading]
  );

  switch (wizard.activeStep) {
    case 0:
      return (
        <DownloadTemplateStep
          displayName={wizard.displayName}
          extractionQuestions={extractionQuestions}
          questionsLoading={questionsLoading}
          readOnly={wizardState.isReadOnly}
          selectedQuestionIds={wizard.selectedQuestionIds}
          onDisplayNameChange={wizard.updateDisplayName}
          onSelectedQuestionIdsChange={wizard.selectQuestions}
        />
      );
    case 1:
      return (
        <UploadSpreadsheetStep
          readOnly={wizardState.isReadOnly}
          uploadedFile={wizard.uploadedFile}
          onClearUploadedFile={handleClearUploadedFile}
          onDownloadTemplate={wizard.handleDownloadTemplate}
          onFileSelect={wizard.handleFileSelect}
        />
      );
    case 2:
      if (!wizard.uploadResult) return null;

      return (
        <ImportPreviewStep
          operationError={wizard.operationError}
          overwriteQuestionIds={wizard.overwriteQuestionIds}
          preview={wizard.importPreview}
          previewErrorMessage={wizard.importPreviewError?.message ?? null}
          previewLoading={wizard.importPreviewLoading}
          readOnly={wizardState.isReadOnly}
          setOverwriteQuestionIds={wizard.setOverwriteQuestionIds}
          uploadResult={wizard.uploadResult}
          onClearOperationError={wizard.clearOperationError}
          onReUpload={wizard.goBack}
        />
      );
    default:
      return null;
  }
}

// --- Shared step header ---

function StepHeader({
  description,
  title,
  titleAction,
}: {
  description: string;
  title: string;
  titleAction?: React.ReactNode;
}): JSX.Element {
  const { classes } = useStepHeaderStyles();
  return (
    <div className={classes.root}>
      <Stack direction="row" sx={{ alignItems: 'baseline', gap: 1 }}>
        <Typography className={classes.title}>{title}</Typography>
        {titleAction}
      </Stack>
      <Typography className={classes.description} variant="body2">
        {description}
      </Typography>
    </div>
  );
}

// --- Step 0: Download Template ---

function DownloadTemplateStep({
  displayName,
  extractionQuestions,
  onDisplayNameChange,
  onSelectedQuestionIdsChange,
  questionsLoading,
  readOnly,
  selectedQuestionIds,
}: DownloadTemplateStepProps): JSX.Element {
  const { classes } = useDownloadTemplateStepStyles();
  const { importableQuestions, selectedQuestions } = getDownloadTemplateStepState({
    extractionQuestions,
    selectedQuestionIds,
  });
  const allSelected =
    importableQuestions.length > 0 && selectedQuestions.length === importableQuestions.length;
  const handleToggleSelectAll = useCallback(() => {
    if (allSelected) {
      onSelectedQuestionIdsChange([]);
    } else {
      onSelectedQuestionIdsChange(importableQuestions.map(q => q.id));
    }
  }, [allSelected, importableQuestions, onSelectedQuestionIdsChange]);

  return (
    <div className={classes.content}>
      <StepHeader
        description="Name this import and choose the Aline properties that should become spreadsheet columns."
        title="Template Setup"
      />
      <div className={classes.twoColumnGrid}>
        <div className={classes.formFields}>
          <div className={classes.section}>
            <Typography className={classes.fieldLabel}>Migration Name</Typography>
            <AlineInput
              fullWidth
              disabled={readOnly}
              placeholder="e.g. User Profiles"
              value={displayName}
              onChange={event => onDisplayNameChange(event.target.value)}
            />
          </div>
          <div className={classes.section}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography className={classes.fieldLabel}>
                Aline Properties
                {selectedQuestions.length > 0 && ` · ${selectedQuestions.length} selected`}
              </Typography>
              {!readOnly && importableQuestions.length > 0 && (
                <Link
                  component="button"
                  sx={{ fontSize: 12, fontWeight: 500 }}
                  underline="hover"
                  variant="body2"
                  onClick={handleToggleSelectAll}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </Link>
              )}
            </Stack>
            <Autocomplete
              disableCloseOnSelect
              multiple
              openOnFocus
              disabled={readOnly}
              getOptionLabel={option => option.label}
              loading={questionsLoading}
              options={importableQuestions}
              renderInput={params => (
                <AlineAutocompleteInput
                  {...params}
                  placeholder={selectedQuestionIds.length > 0 ? '' : 'Select AI properties...'}
                />
              )}
              renderOption={(props, option, { selected }) => (
                <li {...props} key={option.id}>
                  <Checkbox checked={selected} size="small" sx={{ mr: 1 }} />
                  {option.label}
                </li>
              )}
              renderValue={(options, getItemProps) => {
                const visibleOptions = options.slice(0, MAX_VISIBLE_PROPERTY_CHIPS);
                const hiddenCount = options.length - visibleOptions.length;

                return (
                  <>
                    {visibleOptions.map((option, index) => {
                      const { key, ...tagProps } = getItemProps({ index });
                      return (
                        <Chip
                          key={key}
                          color="default"
                          label={option.label}
                          size="small"
                          variant="outlined"
                          {...tagProps}
                        />
                      );
                    })}
                    {hiddenCount > 0 && (
                      <Chip
                        color="default"
                        label={`+${hiddenCount}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </>
                );
              }}
              slotProps={{
                chip: {
                  color: 'default',
                  size: 'small',
                  variant: 'outlined',
                },
                paper: {
                  sx: theme => ({
                    '& .MuiAutocomplete-listbox': { maxHeight: 240 },
                    border: `1px solid ${theme.appColors.rule}`,
                  }),
                },
                popper: {
                  modifiers: [{ enabled: false, name: 'flip' }],
                  placement: 'bottom',
                  sx: { zIndex: 1500 },
                },
              }}
              value={selectedQuestions}
              onChange={(_event, newValue) =>
                onSelectedQuestionIdsChange(newValue.map(question => question.id))
              }
            />
          </div>
        </div>
        <Box className={classes.sidebar}>
          <HowItWorksCard />
        </Box>
      </div>
    </div>
  );
}

function HowItWorksCard(): JSX.Element {
  const { classes } = useDownloadTemplateStepStyles();
  return (
    <div className={classes.sidebarCard}>
      <div className={classes.sidebarCardHeader}>
        <Typography sx={{ color: 'text.primary', fontSize: 13, fontWeight: 600 }}>
          How it works
        </Typography>
      </div>
      <Stack sx={{ gap: 1.25 }}>
        {[
          {
            description:
              '— name the migration and select the Aline properties to include. The next step generates an .xlsx template with them as columns',
            title: 'Set Up',
          },
          {
            description:
              '— open the template in Excel and add a value for each document you want to update',
            title: 'Fill Information',
          },
          {
            description:
              '— upload the completed file to preview the parsed values, review any conflicts, and confirm the results before importing',
            title: 'Validate',
          },
        ].map((step, index) => (
          <div key={step.title} className={classes.howItWorksItem}>
            <div className={classes.howItWorksNumber}>{index + 1}</div>
            <Typography sx={{ color: 'text.secondary', fontSize: 12.5, lineHeight: 1.4 }}>
              <strong>{step.title}</strong> {step.description}
            </Typography>
          </div>
        ))}
      </Stack>
    </div>
  );
}

// --- Step 1: Upload Spreadsheet ---

function UploadSpreadsheetStep({
  onClearUploadedFile,
  onDownloadTemplate,
  onFileSelect,
  readOnly,
  uploadedFile,
}: UploadSpreadsheetStepProps): JSX.Element {
  const { classes } = useUploadSpreadsheetStepStyles();

  /**
   * Rejected files are forwarded to onFileSelect too: its type/size validation
   * is the single source of the user-facing error notifications, so rejected
   * drops surface the same message as picks from the file dialog.
   */
  const handleDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      const file = acceptedFiles[0] ?? fileRejections[0]?.file;
      if (file) onFileSelect(file);
    },
    [onFileSelect]
  );

  const hasUploadedFile = Boolean(uploadedFile);
  const { getInputProps, getRootProps, isDragActive, open } = useDropzone({
    accept: { [SPREADSHEET_MIME_TYPE]: ['.xlsx'] },
    disabled: readOnly,
    multiple: false,
    // With a file already selected, the links handle clicks/keyboard instead.
    noClick: hasUploadedFile,
    noKeyboard: hasUploadedFile,
    onDrop: handleDrop,
  });

  return (
    <div className={classes.content}>
      <StepHeader
        description="Add your data to the Excel template and upload the file to validate and preview before importing."
        title="Upload Spreadsheet"
        titleAction={
          !readOnly && (
            <Link
              component="button"
              sx={{
                alignItems: 'center',
                display: 'inline-flex',
                fontSize: 13,
                fontWeight: 500,
                gap: 0.5,
              }}
              underline="hover"
              onClick={onDownloadTemplate}
            >
              <Download size={14} />
              Download template
            </Link>
          )
        }
      />
      <div
        {...getRootProps({
          className: clsx(
            classes.dropzone,
            uploadedFile && classes.dropzoneStatic,
            !uploadedFile && isDragActive && classes.dropzoneActive
          ),
          style: readOnly ? { cursor: 'default' } : undefined,
        })}
      >
        <input {...getInputProps()} />
        {uploadedFile ? (
          <>
            <Box className={classes.fileIconCircle}>
              <FileSpreadsheet size={34} strokeWidth={1.5} />
            </Box>
            <Stack sx={{ alignItems: 'center', gap: 0.5, maxWidth: '100%', px: 2 }}>
              <Typography noWrap className={classes.dropzoneTitle} sx={{ maxWidth: '100%' }}>
                {uploadedFile.name}
              </Typography>
              <Typography className={classes.fileMeta}>
                {formatFileSize(uploadedFile.size)} — ready to validate
              </Typography>
            </Stack>
            {!readOnly && (
              <Stack direction="row" sx={{ alignItems: 'center', gap: 2.5, mt: 0.5 }}>
                <Link
                  component="button"
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    fontSize: 13,
                    fontWeight: 500,
                    gap: 0.5,
                  }}
                  underline="hover"
                  onClick={open}
                >
                  <CloudUpload size={14} />
                  Replace file
                </Link>
                <Link
                  component="button"
                  sx={{
                    alignItems: 'center',
                    color: 'text.secondary',
                    display: 'inline-flex',
                    fontSize: 13,
                    fontWeight: 500,
                    gap: 0.5,
                  }}
                  underline="hover"
                  onClick={onClearUploadedFile}
                >
                  <X size={14} />
                  Remove
                </Link>
              </Stack>
            )}
          </>
        ) : (
          <>
            <Box className={classes.iconCircle}>
              <CloudUpload size={34} strokeWidth={1.5} />
            </Box>
            <Stack sx={{ alignItems: 'center', gap: 0.5 }}>
              <Typography className={classes.dropzoneTitle}>
                Drag and drop your .xlsx file here
              </Typography>
              <Typography className={classes.dropzoneDescription} variant="body2">
                or <span className={classes.dropzoneBrowse}>browse</span> from your computer
              </Typography>
              <Typography className={classes.dropzoneHint} sx={{ mt: 0.5 }}>
                .xlsx — up to {MAX_METADATA_IMPORT_UPLOAD_MB} MB
              </Typography>
            </Stack>
          </>
        )}
      </div>
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

// --- Step 2: Preview and Validation ---

interface ImportPreviewStepProps {
  onClearOperationError: () => void;
  operationError: string | null;
  overwriteQuestionIds: Set<string>;
  preview: DocumentMetadataImportPreviewFieldsFragment | null;
  previewErrorMessage: string | null;
  previewLoading: boolean;
  setOverwriteQuestionIds: (questionIds: Set<string>) => void;
  uploadResult: DocumentMetadataImportFieldsFragment;
  onReUpload?: () => void;
  readOnly?: boolean;
}

interface ImportPreviewColumnRow {
  columnName: string;
  conflictCount: number;
  hasErrors: boolean;
  hasWarnings: boolean;
  id: string;
  statusKey: PreviewStatusKey;
  statusLabel: string;
  typeLabel: string;
}

type PreviewStatusKey = 'error' | 'valid' | 'warning';

const PREVIEW_STATUS = {
  error: { label: 'Has errors' },
  valid: { label: 'Valid' },
  warning: { label: 'Conflict detected' },
} satisfies Record<PreviewStatusKey, { label: string }>;

const useImportPreviewStepStyles = makeStyles()(theme => ({
  content: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1),
    minHeight: 0,
    width: '100%',
  },
  previewGridContainer: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 160,
  },
  previewGridSection: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minHeight: 0,
  },
  statsRow: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  statsValue: {
    color: theme.appColors.iconsType,
    fontSize: 13,
    fontWeight: 600,
  },
}));

function ImportPreviewStep({
  onClearOperationError,
  onReUpload,
  operationError,
  overwriteQuestionIds,
  preview,
  previewErrorMessage,
  previewLoading,
  readOnly,
  setOverwriteQuestionIds,
  uploadResult,
}: ImportPreviewStepProps): JSX.Element {
  const { classes } = useImportPreviewStepStyles();
  const theme = useTheme();
  const { columnRows, hasOverwriteWarnings, stats, validationAlert } = getImportPreviewStepState({
    onClearOperationError,
    onReUpload,
    operationError,
    preview,
    previewErrorMessage,
    previewLoading,
    readOnly: Boolean(readOnly),
    uploadResult,
  });
  const overwritableQuestionIds = columnRows
    .filter(row => row.hasWarnings && !row.hasErrors)
    .map(row => row.id);
  const overwriteSelectionModel = React.useMemo<GridRowSelectionModel>(
    () => ({ ids: new Set(overwriteQuestionIds), type: 'include' }),
    [overwriteQuestionIds]
  );
  const gridColumns = getImportPreviewColumns({
    hasOverwriteWarnings,
    readOnly: Boolean(readOnly),
    statusColors: {
      error: theme.palette.error.main,
      valid: theme.palette.success.main,
      warning: theme.palette.warning.main,
    },
  });

  return (
    <div className={classes.content}>
      <AlineAlert
        action={validationAlert.action}
        severity={validationAlert.severity}
        onClose={validationAlert.onClose}
      >
        {validationAlert.message}
      </AlineAlert>
      <StepHeader
        description="Each Aline Property column and its validation status after parsing the uploaded file."
        title="Preview and Validation"
      />
      <div className={classes.statsRow}>
        <Typography className={classes.statsValue}>
          {getPluralizedCount(stats.totalRows, 'row')}
        </Typography>
        ·
        <Typography
          className={classes.statsValue}
          sx={stats.conflictRows > 0 ? { color: theme.appColors.accent5 } : undefined}
        >
          {getPluralizedCount(stats.conflictRows, 'conflict')}
        </Typography>
        ·
        <Typography
          className={classes.statsValue}
          sx={stats.errorRows > 0 ? { color: theme.palette.error.main } : undefined}
        >
          {getPluralizedCount(stats.errorRows, 'error')}
        </Typography>
      </div>
      <div className={classes.previewGridSection}>
        <Box className={classes.previewGridContainer}>
          <DataGridPremium<ImportPreviewColumnRow>
            disableColumnMenu
            disableColumnReorder
            disableColumnResize
            disableRowSelectionOnClick
            hideFooter
            checkboxSelection={hasOverwriteWarnings}
            columns={gridColumns}
            density="compact"
            isRowSelectable={({ row }) => !readOnly && row.hasWarnings && !row.hasErrors}
            loading={previewLoading}
            rowSelectionModel={overwriteSelectionModel}
            rows={columnRows}
            slots={{
              baseCheckbox: AlineCheckbox,
            }}
            sx={createDataGridPremiumFooterlessPaperStyles}
            onRowSelectionModelChange={model => {
              const selectedQuestionIds =
                model.type === 'exclude'
                  ? overwritableQuestionIds.filter(id => !model.ids.has(id))
                  : Array.from(model.ids, id => String(id)).filter(id =>
                      overwritableQuestionIds.includes(id)
                    );
              setOverwriteQuestionIds(new Set(selectedQuestionIds));
            }}
          />
        </Box>
      </div>
    </div>
  );
}

function getImportPreviewColumns(options: {
  hasOverwriteWarnings: boolean;
  readOnly: boolean;
  statusColors: Record<PreviewStatusKey, string>;
}): GridColDef<ImportPreviewColumnRow>[] {
  const { hasOverwriteWarnings, readOnly, statusColors } = options;
  const columns: GridColDef<ImportPreviewColumnRow>[] = [
    {
      field: 'columnName',
      flex: 3,
      headerName: 'Aline Property',
      minWidth: 160,
    },
    {
      field: 'typeLabel',
      flex: 1.5,
      headerName: 'Type',
      minWidth: 80,
    },
    {
      field: 'statusLabel',
      flex: 2,
      headerName: 'Status',
      minWidth: 120,
      renderCell: params => (
        <Typography
          sx={{
            color: statusColors[params.row.statusKey],
            fontSize: 14,
            fontWeight: 600,
          }}
          variant="body2"
        >
          {params.row.statusLabel}
        </Typography>
      ),
    },
  ];

  if (hasOverwriteWarnings) {
    columns.push({
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      align: 'right',
      field: GRID_CHECKBOX_SELECTION_FIELD,
      headerAlign: 'right',
      headerName: 'Overwrite',
      renderCell: params => {
        if (readOnly || !params.row.hasWarnings || params.row.hasErrors) return null;

        return (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1, width: '100%' }}>
            {GRID_CHECKBOX_SELECTION_COL_DEF.renderCell?.(params)}
          </Box>
        );
      },
      renderHeader: params => (
        <AlineTooltip
          arrow
          placement="top"
          title="There is conflicting data in Aline for this property. Check to allow the migration to update the conflicting data. Unchecked conflicting properties will be skipped. This action cannot be undone."
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gap: 0.5,
              justifyContent: 'flex-end',
              pr: 1,
              width: '100%',
            }}
          >
            <Typography sx={{ fontWeight: 600 }} variant="body2">
              Overwrite
            </Typography>
            {GRID_CHECKBOX_SELECTION_COL_DEF.renderHeader?.(params)}
          </Box>
        </AlineTooltip>
      ),
      sortable: false,
      width: 150,
    });
  }

  return columns;
}

function getImportPreviewStepState(options: {
  onClearOperationError: () => void;
  operationError: string | null;
  preview: DocumentMetadataImportPreviewFieldsFragment | null;
  previewErrorMessage: string | null;
  previewLoading: boolean;
  readOnly: boolean;
  uploadResult: DocumentMetadataImportFieldsFragment;
  onReUpload?: () => void;
}) {
  const {
    onClearOperationError,
    onReUpload,
    operationError,
    preview,
    previewErrorMessage,
    previewLoading,
    readOnly,
    uploadResult,
  } = options;
  const summary = uploadResult.summary;
  const columnRows: ImportPreviewColumnRow[] = (preview?.columns ?? []).map(column => {
    const hasErrors = column.errorCount > 0;
    const hasWarnings = column.warningCount > 0;
    const statusKey = getPreviewStatusKey({ hasErrors, hasWarnings });

    return {
      columnName: column.title || 'Untitled',
      conflictCount: column.warningCount,
      hasErrors,
      hasWarnings,
      id: column.questionId,
      statusKey,
      statusLabel:
        statusKey === 'warning'
          ? getPluralizedCount(column.warningCount, 'row conflict')
          : PREVIEW_STATUS[statusKey].label,
      typeLabel: QUESTION_TYPE_LABELS[column.type],
    };
  });
  const emptyColumns = summary?.emptyColumns ?? [];
  const conflictRows = preview?.conflictRows ?? 0;
  const errorRows = preview?.errorRows ?? summary?.errorRows ?? 0;

  return {
    columnRows,
    hasOverwriteWarnings: columnRows.some(row => row.hasWarnings),
    stats: {
      conflictRows,
      errorRows,
      totalRows: preview?.totalRows ?? summary?.totalRows ?? 0,
    },
    validationAlert: getImportPreviewValidationAlert({
      emptyColumns,
      errorRows,
      onClearOperationError,
      onReUpload,
      operationError,
      previewErrorMessage,
      previewLoading,
      readOnly,
    }),
  };
}

function getImportPreviewValidationAlert(options: {
  emptyColumns: string[];
  errorRows: number;
  onClearOperationError: () => void;
  operationError: string | null;
  previewErrorMessage: string | null;
  previewLoading: boolean;
  readOnly: boolean;
  onReUpload?: () => void;
}): ImportValidationAlert {
  const {
    emptyColumns,
    errorRows,
    onClearOperationError,
    onReUpload,
    operationError,
    previewErrorMessage,
    previewLoading,
    readOnly,
  } = options;

  if (operationError) {
    return {
      message: operationError,
      onClose: onClearOperationError,
      severity: 'error',
    };
  }

  if (previewErrorMessage) {
    return {
      message: previewErrorMessage,
      severity: 'error',
    };
  }

  if (previewLoading) {
    return {
      message: 'Loading validation preview...',
      severity: 'success',
    };
  }

  if (errorRows > 0) {
    return {
      action: onReUpload ? (
        <AlineButton color="inherit" onClick={onReUpload}>
          Re-Upload
        </AlineButton>
      ) : undefined,
      message: `${getPluralizedCount(errorRows, 'row')} have errors. Fix the spreadsheet${
        onReUpload ? ', then upload the corrected file.' : ' and re-upload.'
      }`,
      severity: 'error',
    };
  }

  if (!readOnly && emptyColumns.length > 0) {
    return {
      message: getEmptyColumnsWarningMessage(emptyColumns),
      severity: 'warning',
    };
  }

  return {
    message: 'Your file is valid and ready to import!',
    severity: 'success',
  };
}

// --- Shared helpers ---

function getPreviewStatusKey(options: {
  hasErrors: boolean;
  hasWarnings: boolean;
}): PreviewStatusKey {
  const { hasErrors, hasWarnings } = options;
  if (hasErrors) return 'error';
  if (hasWarnings) return 'warning';
  return 'valid';
}

function getPluralizedCount(count: number, label: string): string {
  return `${count} ${label}${count === 1 ? '' : 's'}`;
}

function getImportDataWizardState(options: {
  activeStep: number;
  confirming: boolean;
  displayName: string;
  generating: boolean;
  selectedQuestionIds: string[];
  uploadedFile: File | null;
  uploading: boolean;
  uploadResult: DocumentMetadataImportFieldsFragment | null;
}): ImportDataWizardState {
  const {
    activeStep,
    confirming,
    displayName,
    generating,
    selectedQuestionIds,
    uploadResult,
    uploadedFile,
    uploading,
  } = options;
  const isProcessing = generating || uploading || confirming;
  const isReadOnly =
    Boolean(uploadResult) &&
    uploadResult?.status !== DocumentMetadataImportStatus.ReadyToImport &&
    uploadResult?.status !== DocumentMetadataImportStatus.Draft &&
    uploadResult?.status !== DocumentMetadataImportStatus.Failed;

  return {
    canConfirmImport:
      !isReadOnly && uploadResult?.status === DocumentMetadataImportStatus.ReadyToImport,
    canGoNextFromDownload:
      Boolean(displayName.trim()) && selectedQuestionIds.length > 0 && !isProcessing,
    canGoNextFromUpload: Boolean(uploadedFile) && !isProcessing,
    confirming,
    generating,
    isFailedImport: uploadResult?.status === DocumentMetadataImportStatus.Failed,
    isProcessing,
    isReadOnly,
    showDownloadFooter: activeStep === 0,
    showPreviewFooter: activeStep === 2,
    showUploadFooter: activeStep === 1,
    title: 'Data Migration',
    uploading,
    wizardSteps: WIZARD_STEPS,
  };
}

function WizardProgress({ activeStep, allCompleted, steps }: WizardProgressProps): JSX.Element {
  const { classes, cx } = useWizardProgressStyles();
  const effectiveStep = allCompleted ? steps.length : activeStep;

  return (
    <div className={classes.root}>
      {steps.map((step, index) => {
        const isCompleted = index < effectiveStep;
        const isActive = index === effectiveStep;

        return (
          <React.Fragment key={step.label}>
            {index > 0 && (
              <div
                className={cx(classes.connector, index <= effectiveStep && classes.connectorDone)}
              />
            )}
            <div className={classes.step}>
              <div
                className={cx(
                  classes.indicator,
                  isActive && classes.indicatorActive,
                  isCompleted && classes.indicatorCompleted
                )}
              >
                {isCompleted ? <Check size={12} strokeWidth={3} /> : index + 1}
              </div>
              <Typography
                className={cx(
                  classes.label,
                  isActive && classes.labelActive,
                  isCompleted && classes.labelCompleted
                )}
              >
                {step.label}
              </Typography>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

// --- Footer ---

function ImportDataWizardFooter({ actions, state }: ImportDataWizardFooterProps): JSX.Element {
  const { classes } = useFooterStyles();
  const { onBack, onClose, onConfirmImport, onNextFromDownload, onUploadAndValidate } = actions;
  const {
    canConfirmImport,
    canGoNextFromDownload,
    canGoNextFromUpload,
    confirming,
    generating,
    isFailedImport,
    isProcessing,
    isReadOnly,
    showDownloadFooter,
    showPreviewFooter,
    showUploadFooter,
    uploading,
  } = state;

  // Failed migrations are terminal in the wizard — only offer Close.
  if (isReadOnly || (isFailedImport && showPreviewFooter)) {
    return (
      <DialogActions className={classes.actions} sx={{ p: 0, px: 5 }}>
        <Box />
        <AlineButton
          buttonClassName={classes.footerButton}
          color="primary"
          variant="outlined"
          onClick={onClose}
        >
          Close
        </AlineButton>
      </DialogActions>
    );
  }

  return (
    <DialogActions className={classes.actions} sx={{ p: 0, px: 5 }}>
      {showDownloadFooter && (
        <>
          <Box />
          <AlineButton
            buttonClassName={classes.footerButton}
            color="primary"
            disabled={!canGoNextFromDownload}
            loading={generating}
            variant="contained"
            onClick={onNextFromDownload}
          >
            Next
          </AlineButton>
        </>
      )}
      {showUploadFooter && (
        <>
          <AlineButton
            buttonClassName={classes.footerButton}
            color="primary"
            disabled={isProcessing}
            variant="outlined"
            onClick={onBack}
          >
            Back
          </AlineButton>
          <AlineTooltip title={canGoNextFromUpload || uploading ? '' : 'Upload a file first'}>
            <span>
              <AlineButton
                buttonClassName={classes.footerButton}
                color="primary"
                disabled={!canGoNextFromUpload}
                loading={uploading}
                variant="contained"
                onClick={onUploadAndValidate}
              >
                Validate
              </AlineButton>
            </span>
          </AlineTooltip>
        </>
      )}
      {showPreviewFooter && (
        <>
          <AlineButton
            buttonClassName={classes.footerButton}
            color="primary"
            disabled={isProcessing}
            variant="outlined"
            onClick={onBack}
          >
            Back
          </AlineButton>
          <AlineTooltip
            title={
              !canConfirmImport && !isProcessing
                ? 'Migration is blocked. See the alert message.'
                : ''
            }
          >
            <span>
              <AlineButton
                buttonClassName={classes.footerButton}
                color="primary"
                disabled={!canConfirmImport || isProcessing}
                loading={confirming}
                variant="contained"
                onClick={onConfirmImport}
              >
                Migrate Data
              </AlineButton>
            </span>
          </AlineTooltip>
        </>
      )}
    </DialogActions>
  );
}

function getDownloadTemplateStepState(options: {
  extractionQuestions: ExtractionQuestionOption[];
  selectedQuestionIds: string[];
}) {
  const { extractionQuestions, selectedQuestionIds } = options;
  const importableQuestions: DownloadTemplateQuestionOption[] = extractionQuestions
    .filter(question => question.isActive)
    .map(question => ({
      ...question,
      label: question.title || question.questionText,
    }));

  return {
    importableQuestions,
    selectedQuestions: importableQuestions.filter(question =>
      selectedQuestionIds.includes(question.id)
    ),
  };
}
